<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/packing/edit_package.yml"
  lang="yaml"
></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_harvested_batch')"
    :params="{
      organization_id: organization_id,
      harvested_batch_id: harvested_batch_id,
    }"
  />

  <bread-crumbs
    :labels="[
      module_name,
      harvested_batch.harvest_process?.name,
      harvested_batch.batch_code,
    ]"
  />

  <template v-if="load_general_data">
    <harvested-batch-info :harvested_batch="harvested_batch" />

    <alert-danger class="mt-8" :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package.producer") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="producers.length > 0"
                    :options="producers"
                    label="label"
                    v-model="pkg.producer_id"
                    :is_invalid="isInvalidSelect('producer')"
                  />
                </div>

              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_field") }}
                </label>
                <div class="mt-1" v-if="crop_fields.length > 1">
                  <select-form
                    :options="crop_fields"
                    v-model="pkg.exported_batch.crop_field_id"
                    :is_invalid="isInvalidSelect('crop_field')"
                  />
                </div>
                <dd class="content-data" v-else-if="crop_fields.length == 1">
                  {{ crop_fields[0].name }}
                </dd>
                <div class="space-x-2 mt-2">
                  <template
                    v-for="(certification, index) in crop_field_certifications"
                    :key="index"
                  >
                    <danger-badge
                      v-if="availableCertification(certification)"
                      :label="certification.certification.name"
                    />
                    <success-badge
                      v-else
                      :label="certification.certification.name"
                    />
                  </template>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package.quantity") }}
                </label>
                <div class="mt-1">
                  <input
                    type="number"
                    v-model.trim="pkg.quantity"
                    :class="['input-text', isInvalid('quantity')]"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_varieties.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_variety") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_varieties"
                    v-model="pkg.exporter_process_crop_variety_id"
                    :is_invalid="
                      isInvalidSelect('exporter_process_crop_variety')
                    "
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_sizes.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_size") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_sizes"
                    v-model="pkg.exporter_process_crop_size_id"
                    :is_invalid="isInvalidSelect('exporter_process_crop_size')"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_packages.length > 1"
              >
                <label class="label-form">
                  Cliente
                </label>
                <div class="mt-1">
                  <select-form :options="customers" v-model="customer_id" />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_packages.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_package") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_packages_filtered"
                    v-model="pkg.exporter_process_crop_package_id"
                    :is_invalid="
                      isInvalidSelect('exporter_process_crop_package')
                    "
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_categories.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_category") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_categories"
                    v-model="pkg.exporter_process_crop_category_id"
                    :is_invalid="
                      isInvalidSelect('exporter_process_crop_category')
                    "
                  />
                </div>
              </div>
              <template
                v-if="pkg.exporter_process_crop_package_id && !pkg.exporter_process_brand_id"
              >
                <div
                  class="sm:col-span-3"
                  v-if="exporter_process_brands.length > 1"
                >
                  <label class="label-form">
                    {{ $t("model.package.exporter_process_brand") }}
                  </label>
                  <div class="mt-1">
                    <select-form
                      :options="exporter_process_brands"
                      v-model="pkg.exporter_process_brand_id"
                      :is_invalid="isInvalidSelect('exporter_process_brand')"
                    />
                  </div>
                </div>
              </template>
              <div class="sm:col-span-3" v-if="process_lines.length > 1">
                <label class="label-form">
                  {{ $t("model.package.process_line") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="process_lines"
                    v-model="pkg.process_line_id"
                    :is_invalid="isInvalidSelect('process_line')"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="packing_house_processes.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.exported_batch.packing_house_process") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="packing_house_processes"
                    v-model="pkg.exported_batch.packing_house_process_id"
                    :is_invalid="
                      isInvalidSelect('exported_batch.packing_house_process')
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions space-y-3 my-3 md:space-y-0 md:my-0">
            <white-button
              @click.prevent="goToShowHarvestedBatch()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!charging_submit"
              @click.prevent="update()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/process/packing/manage_package.js";
import RequesterQuery from "@/views/organization/requesters/process/packing/queries_to_package.js";
import HarvestedBatchInfo from "@/components/views/organization/process/packing/HarvestedBatchInfo.vue";

export default {
  name: "OrgProcessPackingEditPackage",
  components: { HarvestedBatchInfo },
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvested_batch: {},
      producerss: [],
      exporter_process_crop_packages: [],
      exporter_process_crop_categories: [],
      exporter_process_crop_varieties: [],
      exporter_process_crop_sizes: [],
      exporter_process_brands: [],
      process_lines: [],
      packing_house_processes: [],
      pkg: {},
      crop_field_certifications: [],
      crop_fields: [],
      customers: [],
      exporter_process_crop_packages_filtered: [],
      customer_id: ''
    };
  },
  props: {
    organization_id: { required: true },
    harvested_batch_id: { required: true },
    package_id: { required: true },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
  computed: {},
  methods: {
    loadData: async function () {
      await this.fetchHarvestedBatch();
      await this.fetchProducers();
      await this.fetchProcessLines();
      await this.fetchExporterProcessCropCategories();
      await this.fetchExporterProcessCropVarieties();
      await this.fetchExporterProcessCropSizes();
      await this.fetchExporterProcessCropPackages();
      await this.fetchExporterProcessBrands();
      await this.fetchPackingHouseProcesses();
      await this.fetchPackage();
      await this.fetchCustomers();
      this.load_general_data = true;
    },
    fetchHarvestedBatch: async function () {
      const response = await Requester.fetchHarvestedBatch(
        this.organization_id,
        this.harvested_batch_id
      );
      this.harvested_batch = response.data;
      this.harvest_process_id = this.harvested_batch.harvest_process_id;
    },
    fetchPackage: async function () {
      const response = await Requester.fetchPackage(
        this.organization_id,
        this.harvested_batch_id,
        this.package_id
      );
      this.pkg = response.data;
    },
    fetchProducers: async function () {
      const response = await RequesterQuery.fetchProducers(
        this.organization_id,
        this.harvested_batch_id
      );
      this.producers = response.data;
      this.producers.forEach(producer => {
        producer.label = `${producer.organization_producer_code} - ${producer.fullname}`;
      });
    },
    fetchCropFields: async function () {
      const response = await RequesterQuery.fetchCropFields(
        this.organization_id,
        this.harvested_batch_id,
        this.pkg.producer_id,
      );
      this.crop_fields = response.data;
      if (this.crop_fields.length == 1) {
        this.pkg.exported_batch.crop_field_id = this.hasOneItem(this.crop_fields);
      }
    },
    fetchProcessLines: async function () {
      const response = await RequesterQuery.fetchProcessLines(
        this.organization_id,
        this.harvested_batch_id
      );
      this.process_lines = response.data;
    },
    fetchExporterProcessCropCategories: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropCategories(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_categories = response.data;
    },
    fetchExporterProcessCropVarieties: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropVarieties(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_varieties = response.data;
    },
    fetchExporterProcessCropSizes: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropSizes(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_sizes = response.data;
    },
    fetchExporterProcessCropPackages: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropPackages(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_packages = response.data;
    },
    fetchExporterProcessBrands: async function () {
      const response = await RequesterQuery.fetchExporterProcessBrands(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_brands = response.data;
    },
    fetchPackingHouseProcesses: async function () {
      const response = await RequesterQuery.fetchPackingHouseProcesses(
        this.organization_id,
        this.harvested_batch_id
      );
      this.packing_house_processes = response.data;
    },
    fetchOrganizationCropField: async function () {
      const response = await RequesterQuery.fetchOrganizationCropField(
        this.organization_id,
        this.pkg.exported_batch.crop_field_id
      );
      if (
        !this.pkg.process_line_id &&
        this.process_lines
          .map(pl => pl.id)
          .includes(response.data.process_line_id)
      )
        this.pkg.process_line_id = response.data.process_line_id;
    },
    fetchCropFieldCertifications: async function () {
      const response = await RequesterQuery.fetchCropFieldCertifications(
        this.organization_id,
        this.pkg.exported_batch.crop_field_id
      );
      this.crop_field_certifications = response.data.sort(
        (a, b) =>
          this.availableCertification(a) - this.availableCertification(b)
      );
    },
    fetchCustomers: async function() {
      const response = await RequesterQuery.fetchCustomers(
        this.organization_id,
        this.harvested_batch_id
      );
      this.customers = [...response.data, { id: null, name: 'Sin cliente' }];
      this.customer_id = this.exporter_process_crop_packages.find(
        (el) => el.id == this.pkg.exporter_process_crop_package_id
      )?.customer_id;
    },
    update: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.update(
          this.organization_id,
          this.harvested_batch_id,
          this.package_id,
          this.pkg
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowHarvestedBatch();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.charging_submit = false;
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    },
    availableCertification: function (certification) {
      return (
        this.daysUntilExpirationDate(certification.expiration_date) < 0 ||
        certification.suspended
      );
    },
    goToShowHarvestedBatch: function () {
      this.$router.push({
        name: this.routeName("show_harvested_batch"),
        params: {
          organization_id: this.organization_id,
          harvested_batch_id: this.harvested_batch_id,
        },
      });
    },
  },
  watch: {
    customer_id: function (value) {
      this.exporter_process_crop_packages_filtered =
        this.exporter_process_crop_packages.filter(
          (item) => item.customer_id == value
        );
      if (
        !this.exporter_process_crop_packages_filtered.some(
          (item) => item.id === this.pkg.exporter_process_crop_package_id
        )
      ) {
        this.pkg.exporter_process_crop_package_id = null;
      }
    },
    "pkg.producer_id": function (new_value) {
      if (new_value) {
        this.pkg.exported_batch.crop_field_id = null;
        this.fetchCropFields();
      }
    },
    "pkg.exported_batch.crop_field_id": function (new_value) {
      this.pkg.process_line_id = null;
      if (new_value) {
        this.fetchOrganizationCropField();
        this.fetchCropFieldCertifications();
      }
    },
    "pkg.exporter_process_crop_package_id": function (
      exporter_process_crop_package_id
    ) {
      if (!this.load_general_data) return;
      if (!exporter_process_crop_package_id) {
        this.pkg.exporter_process_brand_id = null;
        return;
      }

      let exporter_process_corp_package =
        this.exporter_process_crop_packages.find(
          (exp_process_crop_package) =>
            exp_process_crop_package.id == exporter_process_crop_package_id
        );

      if (exporter_process_corp_package) {
        this.pkg.exporter_process_brand_id =
          exporter_process_corp_package?.exporter_process_brand_id;
      }
    },
  },
};
</script>
